import { cameraAppearance } from './cameraAppearance.js';
import { WALL_MOUNT_OFFSET, WALL_THICKNESS } from './constants.js';

export const MOUNT_KINDS = {
  wall: 'Wall · direct / articulated bracket',
  'wall-pendant': 'Wall arm · hanging camera',
  ceiling: 'Ceiling / soffit · surface mount',
  'ceiling-pendant': 'Ceiling · pendant drop',
  'wall-upright': 'Wall shelf · upright camera',
  pole: 'Exterior pole · bracket adapter',
  'pole-pendant': 'Exterior pole · hanging arm adapter'
};
const defaults = {
  bullet: ['wall', 'ceiling', 'ceiling-pendant', 'pole'], box: ['wall', 'ceiling', 'ceiling-pendant', 'pole'],
  turret: ['wall', 'ceiling', 'ceiling-pendant', 'pole'], dome: ['wall', 'ceiling', 'wall-pendant', 'ceiling-pendant', 'pole', 'pole-pendant'],
  ptz: ['wall-pendant', 'ceiling-pendant', 'pole-pendant'],
  fisheye: ['wall', 'ceiling', 'ceiling-pendant', 'pole'], 'multi-sensor': ['wall-pendant', 'ceiling', 'ceiling-pendant', 'pole-pendant']
};

// Reviewed exceptions apply to existing library records and saved snapshots.
// User-entered model restrictions take precedence. Generic type options are
// planning assumptions, never a claim that a particular adapter is compatible.
const reviewed = [
  { manufacturer: /axis/i, model: /^P3265-LVE$/i, kinds: ['wall', 'ceiling', 'wall-pendant', 'ceiling-pendant'], source: 'https://www.axis.com/dam/public/0c/b6/80/datasheet-axis-p3265-lve-dome-camera-en-US-367666.pdf', note: 'Pendant installations require a compatible pendant kit and bracket.' },
  { manufacturer: /axis/i, model: /^Q6315-LE$/i, kinds: ['wall-pendant', 'ceiling-pendant'], source: 'https://www.axis.com/products/axis-q6315-le/support' },
  { manufacturer: /axis/i, model: /^M4338-PLVE$/i, kinds: ['wall', 'ceiling'], source: 'https://help.axis.com/en-us/axis-m4338-plve' },
  { manufacturer: /reolink/i, model: /^RLC-820A$/i, kinds: ['wall', 'ceiling'], source: 'https://support.reolink.com/articles/900000597443-How-to-Install-Reolink-Dome-Cameras/' },
  { manufacturer: /reolink/i, model: /^RLC-823A$/i, kinds: ['wall-pendant', 'ceiling-pendant'], source: 'https://support.reolink.com/articles/360004396414-How-to-Install-Vertical-Bracket-for-RLC-423-RLC-823A/', note: 'Ceiling installation replaces the wall bracket with the compatible vertical bracket.' },
  { manufacturer: /axis/i, model: /^Q8665-LE$/i, kinds: ['wall-upright'], source: 'https://www.axis.com/products/axis-q8665-le/support' }
];
export function mountingProfile(model = {}) {
  model ||= {};
  const custom = model.details?.mounting;
  const kinds = custom?.kinds?.filter(k => Object.hasOwn(MOUNT_KINDS, k));
  if (kinds?.length) return { ...custom, kinds: [...new Set(kinds)], reviewed: !!custom.source };
  const known = reviewed.find(r => r.manufacturer.test(model.manufacturer || '') && r.model.test(model.modelNumber || model.name || ''));
  if (known) {
    const adapters = [...(known.kinds.includes('ceiling') && !known.kinds.includes('ceiling-pendant') ? ['ceiling-pendant'] : []),
      ...(known.kinds.includes('wall') ? ['pole'] : []), ...(known.kinds.includes('wall-pendant') ? ['pole-pendant'] : [])];
    return { ...known, kinds: [...known.kinds, ...adapters], adapterKinds: adapters, reviewed: true };
  }
  return { kinds: defaults[cameraAppearance(model).type], reviewed: false, source: '' };
}
export const isCeilingKind = kind => kind === 'ceiling' || kind === 'ceiling-pendant';
export const isPoleKind = kind => kind === 'pole' || kind === 'pole-pendant';
export function defaultMountKind(model = {}) {
  model ||= {};
  const profile = mountingProfile(model);
  if (profile.kinds.includes(profile.preferred)) return profile.preferred;
  return (model.mount === 'ceil' ? ceilingMountKind(model) : wallMountKind(model)) || profile.kinds[0];
}
export const cameraMountKind = (model, camera = {}) => Object.hasOwn(MOUNT_KINDS, camera.mountKind) ? camera.mountKind : defaultMountKind(model);
export const isCeilingCamera = (S, camera) => isCeilingKind(cameraMountKind(S.models?.find(m => m.id === camera.modelId), camera));
export const isPoleCamera = (S, camera) => isPoleKind(cameraMountKind(S.models?.find(m => m.id === camera.modelId), camera));
export const placementMountKind = (store, model) => mountingProfile(model).kinds.includes(store.newMountKind) ? store.newMountKind : defaultMountKind(model);
// An explicit model preference wins within the requested support family.
// Otherwise use a supported surface mount before adding a pendant accessory.
function preferredMount(model, order) {
  const profile = mountingProfile(model);
  if (order.includes(profile.preferred) && profile.kinds.includes(profile.preferred)) return profile.preferred;
  return order.find(k => profile.kinds.includes(k));
}
export const wallMountKind = model => preferredMount(model, cameraAppearance(model).type === 'ptz' ? ['wall-pendant', 'wall-upright', 'wall'] : ['wall', 'wall-pendant', 'wall-upright']);
export const ceilingMountKind = (model, suspended = false) => preferredMount(model, suspended ? ['ceiling-pendant'] : ['ceiling', 'ceiling-pendant']);
export const poleMountKind = model => mountingProfile(model).kinds.find(isPoleKind);
export const allowsCeilings = cfg => ['both', 'ceilings', 'all'].includes(cfg.mounts);
export const allowsPoles = cfg => ['poles', 'all'].includes(cfg.mounts);
export const allowsWalls = cfg => !['ceilings', 'poles'].includes(cfg.mounts);
export const supportsPlanningMount = (model, cfg) => !!(allowsWalls(cfg) && wallMountKind(model) || allowsPoles(cfg) && poleMountKind(model) || allowsCeilings(cfg) && mountingProfile(model).kinds.some(isCeilingKind));
export const pendantDrop = camera => camera.mountKind === 'ceiling-pendant' ? Math.min(199, Math.max(0.1, Number.isFinite(camera.mountDrop) ? camera.mountDrop : 0.3)) : 0;
export function mountingFingerprint(model, camera) {
  const kind = cameraMountKind(model, camera);
  return [kind, pendantDrop({ ...camera, mountKind: kind }), isCeilingKind(kind) ? camera.mountSurface || '' : isPoleKind(kind) ? [camera.mountPoleId || '', camera.mountAzimuth ?? 0] : '', mountingProfile(model).kinds];
}

/** Lens-to-support clearance of the representative housing, in metres. */
export function ceilingClearance(model) {
  const { type, scale } = cameraAppearance(model);
  return ({ bullet: 0.22, box: 0.20, turret: 0.125, dome: 0.12, ptz: 0.28, fisheye: 0.06, 'multi-sensor': 0.125 }[type]) * scale;
}

/** Representative lens clearance, not a manufacturer's bracket dimension.
 * Direct housings sit on the face; hanging PTZs need room behind the head. */
export function wallMountOffset(model, camera = {}, wall = {}) {
  const { type, scale } = cameraAppearance(model), kind = cameraMountKind(model, camera);
  const face = (wall.thickness ?? WALL_THICKNESS) / 2;
  if (kind === 'wall' && ['dome', 'turret'].includes(type)) return face + ceilingClearance(model);
  return face + Math.max(WALL_MOUNT_OFFSET - WALL_THICKNESS / 2, type === 'ptz' ? 0.23 * scale : 0);
}

export function wallPlateRise(model, camera = {}) {
  const { type, scale } = cameraAppearance(model), kind = cameraMountKind(model, camera);
  return (type === 'ptz' ? 0.253 : type === 'multi-sensor' ? 0.103 : 0) * scale * (kind === 'wall-upright' ? -1 : 1);
}
